import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../context/UserContext";
import { RouterPath } from "../../assets/dictionary/RouterPath";
import DataService from "../../context/ProfilService";
import { toast } from "sonner";
import { trackEvent } from "../../lib/matomo";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import axios from "axios";
import myAppConfig from "../../config";


import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { ArrowLeft, User, CreditCard, Bell, Trash2, Save, Edit3, Shield, AlertTriangle } from "lucide-react";

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, updateUser } = useUser();
  
  // États pour le formulaire
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email_notification: true
  });
  
  // États pour l'édition
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPortalLoading, setIsPortalLoading] = useState(false);
  
  // États pour la suppression
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Charger les données utilisateur
  useEffect(() => {
    if (user) {
      setFormData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        email_notification: user.email_notification ?? true
      });
    }
  }, [user]);

  // Gérer les changements du formulaire
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Sauvegarder les modifications
  const handleSave = async () => {
    if (!formData.first_name.trim() || !formData.last_name.trim()) {
      toast.error("Le prénom et le nom sont obligatoires");
      return;
    }

    setIsLoading(true);
    try {
      await DataService.updateMyInfo(formData);
      await updateUser(); // Rafraîchir les données utilisateur
      setIsEditing(false);
      toast.success("Profil mis à jour avec succès");
      try { trackEvent("profile", "profile_updated"); } catch(_) {}
    } catch (error) {
      console.error("Erreur lors de la mise à jour:", error);
      toast.error("Erreur lors de la mise à jour du profil");
      try { trackEvent("profile", "profile_update_error"); } catch(_) {}
    } finally {
      setIsLoading(false);
    }
  };

  // Supprimer le profil
  const handleDeleteProfile = async () => {
    if (deleteConfirmation !== "supprimer") {
      toast.error("Veuillez taper 'supprimer' pour confirmer");
      return;
    }

    setIsDeleting(true);
    try {
      await DataService.deleteProfile();
      toast.success("Profil supprimé avec succès");
      try { trackEvent("profile", "profile_deleted"); } catch(_) {}
      navigate(RouterPath.LOGIN);
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast.error("Erreur lors de la suppression du profil");
      try { trackEvent("profile", "profile_delete_error"); } catch(_) {}
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
      setDeleteConfirmation("");
    }
  };

  // Annuler l'édition
  const handleCancelEdit = () => {
    setFormData({
      first_name: user?.first_name || "",
      last_name: user?.last_name || "",
      email_notification: user?.email_notification ?? true
    });
    setIsEditing(false);
  };

  const handleCustomerPortal = async () => {
    setIsPortalLoading(true);
    try {
      const { data } = await axios.post(
        myAppConfig.api.ENDPOINT + "/api/v1/payments/customer-portal",
        {},
        { withCredentials: true }
      );
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Impossible d'accéder au portail client.");
      }
    } catch (e) {
      toast.error("Erreur : Aucun abonnement en cours.");
    } finally {
      setIsPortalLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen relative app-page-gradient flex-col">
      {/* Header Sticky */}
      <header className="w-full border-b border-border/20 bg-card/80 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-6xl mx-auto flex items-center gap-4 px-4 py-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(-1)}
            className="hover:bg-accent/50 text-foreground inline-flex items-center"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            <span>Retour</span>
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Profil Utilisateur</h1>
            <p className="text-muted-foreground">Gérez vos informations personnelles</p>
          </div>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto space-y-6">
          {/* Informations principales */}
          <Card className="border-border/50 bg-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <User className="h-5 w-5" />
                Informations personnelles
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="first_name">Prénom</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => handleInputChange("first_name", e.target.value)}
                    disabled={!isEditing}
                    placeholder="Votre prénom"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name">Nom</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => handleInputChange("last_name", e.target.value)}
                    disabled={!isEditing}
                    placeholder="Votre nom"
                  />
                </div>
              </div>
              
                             <div className="space-y-2">
                 <Label htmlFor="email">Email</Label>
                 <Input
                   id="email"
                   value={user.email}
                   disabled
                   className="bg-muted/50 text-muted-foreground"
                 />
               </div>

              {isEditing ? (
                <div className="flex gap-2 pt-4">
                  <Button onClick={handleSave} disabled={isLoading}>
                    {isLoading ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground mr-2"></div>
                    ) : (
                      <Save className="h-4 w-4 mr-2" />
                    )}
                    Sauvegarder
                  </Button>
                  <Button variant="outline" onClick={handleCancelEdit}>
                    Annuler
                  </Button>
                </div>
              ) : (
                <Button onClick={() => setIsEditing(true)} variant="outline">
                  <Edit3 className="h-4 w-4 mr-2" />
                  Modifier
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Paramètres */}
          <Card className="border-border/50 bg-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <Bell className="h-5 w-5" />
                Paramètres
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <Label htmlFor="email_notification">Notifications par email</Label>
                  <p className="text-sm text-muted-foreground">
                    Recevoir des notifications par email
                  </p>
                </div>
                                 <Switch
                   id="email_notification"
                   checked={formData.email_notification}
                   onCheckedChange={async (checked) => {
                     // Mettre à jour l'état local
                     handleInputChange("email_notification", checked);
                     
                     // Sauvegarder automatiquement
                     try {
                       await DataService.updateMyInfo({ 
                         email_notification: checked,
                         first_name: user.first_name,
                         last_name: user.last_name
                       });
                       await updateUser(); // Rafraîchir les données utilisateur
                       toast.success("Préférence de notification mise à jour");
                     } catch (error) {
                       console.error("Erreur lors de la mise à jour de la notification:", error);
                       toast.error("Erreur lors de la mise à jour de la notification");
                       // Remettre l'ancienne valeur en cas d'erreur
                       handleInputChange("email_notification", !checked);
                     }
                   }}
                 />
              </div>
            </CardContent>
          </Card>

          {/* Statistiques */}
          <Card className="border-border/50 bg-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <CreditCard className="h-5 w-5" />
                Statistiques
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* <div className="text-center p-4 bg-accent/20 dark:bg-accent/10 rounded-lg border border-border/20">
                  <div className="text-2xl font-bold text-primary">{user.credits || 0}</div>
                  <div className="text-sm text-muted-foreground">Crédits disponibles</div>
                </div> */}
                <div className="text-center p-4 bg-accent/20 dark:bg-accent/10 rounded-lg border border-border/20">
                  <div className="text-2xl font-bold text-primary">
                    {user.last_connexion ? new Date(user.created_at).toLocaleDateString('fr-FR') : 'Jamais'}
                  </div>
                  <div className="text-sm text-muted-foreground">Date d'inscription</div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Abonnement */}
          <Card className="border-border/50 bg-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-foreground">
                <CreditCard className="h-5 w-5" />
                Abonnement
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-center">
                <Button
                  className="bg-muted hover:bg-muted/80 text-foreground"
                  onClick={handleCustomerPortal}
                  disabled={isPortalLoading}
                >
                  <CreditCard className="h-4 w-4 mr-2" />
                  Gérer mes abonnements
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Actions dangereuses */}
          <Card className="border-destructive/20 bg-card/50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                Zone dangereuse
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 bg-destructive/10 dark:bg-destructive/5 rounded-lg border border-destructive/20">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-destructive mt-0.5" />
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-destructive">
                      Supprimer mon compte
                    </p>
                    <p className="text-sm text-foreground dark:text-foreground">
                      Cette action est irréversible. Toutes vos données, conversations et crédits seront définitivement supprimés.
                    </p>
                  </div>
                </div>
              </div>
              
              <Button
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                className="w-full"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer mon compte
              </Button>
            </CardContent>
          </Card>
        </div>
        </div>
      </main>

      {/* Dialog de suppression */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-background border-border">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive">
              Êtes-vous absolument sûr ?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-foreground">
              Cette action ne peut pas être annulée. Cela supprimera définitivement votre compte et toutes vos données.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
                     <div className="space-y-4">
             <div className="p-4 bg-destructive/10 dark:bg-destructive/5 rounded-lg border border-destructive/20">
               <p className="text-sm text-foreground dark:text-foreground mb-2">
                 Pour confirmer, tapez <span className="font-mono font-bold text-destructive">supprimer</span> dans le champ ci-dessous :
               </p>
               <Input
                 value={deleteConfirmation}
                 onChange={(e) => setDeleteConfirmation(e.target.value)}
                 placeholder="Tapez 'supprimer' pour confirmer"
                 className="border-destructive/20 focus:border-destructive text-foreground bg-background"
               />
             </div>
           </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel className="text-foreground border-border hover:bg-accent">
              Annuler
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteProfile}
              disabled={deleteConfirmation !== "supprimer" || isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-destructive-foreground mr-2"></div>
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Supprimer définitivement
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
